import { useEffect, useState } from "react";
import {
  ApiError,
  ConnectionBanner,
  SessionBootstrapResult,
  SessionPayload,
  apiGet,
  connectionBannerFromError,
} from "../../api";
import { abortInFlightGets } from "../../apiDeadline";
import {
  classifySessionError,
  createWakeClock,
  noteWakeRefreshSettled,
  notifyOpenPage,
  reduceWake,
  type WakeClock,
} from "./wakeRecovery";

function applySessionFailure(
  err: unknown,
  setSession: (session: SessionPayload | null) => void,
  setConnectionIssue: (issue: ConnectionBanner | null) => void,
): SessionBootstrapResult {
  const failure = classifySessionError(err);
  if (failure.kind === "offline") {
    setConnectionIssue(failure.connectionIssue);
    return { ok: false, issue: failure.connectionIssue };
  }
  setSession(null);
  setConnectionIssue(null);
  return { ok: false, issue: connectionBannerFromError(err) };
}

export function useSession() {
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionIssue, setConnectionIssue] = useState<ConnectionBanner | null>(null);
  async function applyResult(payload: SessionPayload) {
    setSession(payload);
    setConnectionIssue(null);
  }
  async function refresh(): Promise<SessionBootstrapResult> {
    setLoading(true);
    try {
      const payload = await apiGet<SessionPayload>("/api/spa/session");
      await applyResult(payload);
      return { ok: true, session: payload };
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setSession(null);
        setConnectionIssue(null);
        return { ok: false, issue: connectionBannerFromError(err) };
      }
      return applySessionFailure(err, setSession, setConnectionIssue);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    let cancelled = false;
    apiGet<SessionPayload>("/api/spa/session")
      .then((payload) => {
        if (!cancelled) {
          setSession(payload);
          setConnectionIssue(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        applySessionFailure(err, setSession, setConnectionIssue);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    let clock: WakeClock = createWakeClock();
    let disposed = false;
    const recover = () => {
      abortInFlightGets();
      void refresh().then((result) => {
        if (!disposed) notifyOpenPage(result.ok, (name) => window.dispatchEvent(new Event(name)));
        clock = noteWakeRefreshSettled(clock);
      });
    };
    const onVisibility = () => {
      const next = reduceWake(clock, {
        kind: "visibility",
        state: document.visibilityState === "hidden" ? "hidden" : "visible",
        now: Date.now(),
      });
      clock = next.clock;
      if (next.recover) recover();
    };
    const onOnline = () => {
      const next = reduceWake(clock, {
        kind: "online",
        visible: document.visibilityState === "visible",
        now: Date.now(),
      });
      clock = next.clock;
      if (next.recover) recover();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, []);
  return { session, setSession, loading, refresh, connectionIssue };
}
